import React, { useCallback, useState, useEffect, useRef } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Connection,
  ConnectionMode,
  ReactFlowInstance,
  Panel,
  ReactFlowProvider,
  Viewport
} from 'reactflow';
import 'reactflow/dist/style.css';
import "./workflow.css";
import { useTranslation } from 'react-i18next';
import { useToast } from "@/shared/components/ui/use-toast";
import NodeList from './NodeList';
import { Card } from "@/shared/components/ui/card";
import CustomNode from './CustomNode';
import CustomEdge from './CustomEdge';
import { Button } from "@/shared/components/ui/button";
import { ZoomIn, ZoomOut } from "lucide-react";
import { formatNodeForSchema, formatEdgeForSchema, createNewNode, getDefaultValueForType, extractFields } from '../utils/workflow-utils';
import { PageLoadingSpinner } from '@/shared/components/ui/page-loading-spinner';
import { WorkflowService } from '../services/WorkflowService';
import { NodeDefinitionService } from '../services/NodeDefinitionService';
import { WorkflowTestComponent } from './WorkflowTestComponent';
import { v4 as uuidv4 } from 'uuid';
import { useWorkspace } from '@/features/Workspaces/state/WorkspaceContext';

// Interface para nós do schema do workflow
interface SchemaNode {
  id: string;
  nodeId?: string;
  name?: string;
  title?: string;
  position?: { x: number; y: number };
  config?: Record<string, Record<string, unknown>>;
  settings?: Record<string, unknown>;
  data?: Record<string, unknown>;
  [key: string]: unknown;
}

// Interface para arestas do schema do workflow
interface SchemaEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
  type?: string;
  input?: unknown;
  data?: Record<string, unknown>;
  [key: string]: unknown;
}

// Interface para o objeto de configurações do workflow
interface WorkflowSettings {
  nodes: Record<string, unknown>[];
  edges: Record<string, unknown>[];
  organizationId?: string;
  workspaceId?: string;
  [key: string]: unknown; // Para permitir outras propriedades
}

const nodeTypes = {
  customNode: CustomNode,
};

const edgeTypes = {
  smoothEdge: CustomEdge,
};

// Usando UUID direto sem concatenar com "node_"
const getId = () => uuidv4();

const isUuid = (value: string | undefined): value is string =>
  !!value && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(value);

// Configuração inicial do viewport
const defaultViewport: Viewport = {
  x: 0,
  y: 0,
  zoom: 0.85,
};

// Tipo para o ref que expõe a função saveWorkflow
export interface WorkflowEditorRef {
  saveWorkflow: () => Promise<void>;
}

interface WorkflowEditorProps {
  workflowId: string;
  onWorkflowChange?: () => void;
  onUpdate?: (hasChanges: boolean) => void;
  organizationId?: string;
  workspaceId?: string;
  saveRef?: React.MutableRefObject<WorkflowEditorRef | null>;
  refreshKey?: number;
 }

function WorkflowEditorContent({
  workflowId,
  onWorkflowChange,
  onUpdate,
  organizationId,
  workspaceId,
  saveRef,
  refreshKey
}: WorkflowEditorProps) {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { toast } = useToast();
  const { currentWorkspace } = useWorkspace();
  const resolvedOrganizationId = organizationId ?? currentWorkspace?.organization_id;
  const resolvedProductId = workspaceId ?? currentWorkspace?.id;
  const isInitialLoad = useRef(true); // Flag para detectar carregamento inicial
  const lastSavedSchemaRef = useRef<string | null>(null);
  const definitionCacheRef = useRef<Record<string, Record<string, unknown>>>({});

  const loadNodeDefinition = useCallback(async (candidateId?: string, nodeType?: string) => {
    const normalizedType = nodeType?.toLowerCase();
    const cacheKeys = [
      candidateId && isUuid(candidateId) ? candidateId : undefined,
      normalizedType ? `type:${normalizedType}` : undefined
    ].filter(Boolean) as string[];

    for (const cacheKey of cacheKeys) {
      const cachedDefinition = definitionCacheRef.current[cacheKey];
      if (cachedDefinition) {
        return cachedDefinition;
      }
    }

    try {
      let definition = null;

      if (candidateId && isUuid(candidateId)) {
        definition = await WorkflowService.loadNodeDefinition(candidateId);
      } else if (normalizedType) {
        const filterKey = `type:${normalizedType}`;
        const { data, error } = await NodeDefinitionService.getByFilters({ type: normalizedType }, 1, 1);

        if (error) {
          throw error;
        }

        const filteredDefinition = data?.[0];

        if (filteredDefinition?.id && isUuid(filteredDefinition.id as string)) {
          definition = await WorkflowService.loadNodeDefinition(filteredDefinition.id as string);
        } else {
          definition = filteredDefinition;
        }

        if (definition) {
          definitionCacheRef.current[filterKey] = definition;
        }
      }

      if (definition?.id && isUuid(definition.id as string)) {
        definitionCacheRef.current[definition.id as string] = definition;
      }

      return definition;
    } catch (error) {
      console.warn('[WorkflowEditor] Não foi possível carregar definição do nó:', {
        candidateId,
        nodeType,
        error
      });
      return null;
    }
  }, []);

  const buildWorkflowSettings = useCallback((): WorkflowSettings => {
    const formattedNodes = nodes.map((node) => formatNodeForSchema(node));
    const formattedEdges = edges.map((edge) => formatEdgeForSchema(edge));

    return {
      nodes: formattedNodes,
      edges: formattedEdges,
      ...(resolvedOrganizationId ? { organizationId: resolvedOrganizationId } : {}),
      ...(resolvedProductId ? { workspaceId: resolvedProductId } : {})
    };
  }, [nodes, edges, resolvedOrganizationId, resolvedProductId]);

  // Carregar o workflow inicial
  useEffect(() => {
    const loadWorkflow = async () => {
      try {
        setLoading(true);
        isInitialLoad.current = true; // Resetar flag ao carregar

        const settings = await WorkflowService.loadWorkflow(workflowId);

        if (settings) {
          const settingsTyped = settings as { nodes?: SchemaNode[]; edges?: SchemaEdge[]; [key: string]: unknown };
          const { nodes: schemaNodes = [], edges: schemaEdges = [] } = settingsTyped;

          // Criar mapa de posições atuais dos nodes existentes usando o ID canônico
          const currentPositions = new Map<string, { x: number; y: number }>();
          nodes.forEach(node => {
            if (node.position && typeof node.position.x === 'number' && typeof node.position.y === 'number') {
              currentPositions.set(node.id, node.position);
            }
          });

          const loadedNodes = await Promise.all(schemaNodes.map(async (node: SchemaNode, index: number) => {
            const rawNodeId = node.nodeId || (node.data?.nodeId as string | undefined) || node.name;
            const nodeType = ((node.data?.type as string) || node.name || '').toLowerCase();

            const nodeDefinition = await loadNodeDefinition(rawNodeId, nodeType);
            const nodeDefId = nodeDefinition?.id as string | undefined;
            const resolvedNodeId = nodeDefId && isUuid(nodeDefId)
              ? nodeDefId
              : (isUuid(rawNodeId) ? rawNodeId : undefined);

            // Processar configurações com valores padrão
            const definitionSettings = (nodeDefinition?.settings || node.settings) as Record<string, unknown> | undefined;
            const fields = extractFields(definitionSettings || {});
            const defaultValues = fields.reduce((acc: Record<string, unknown>, field: Record<string, unknown>) => {
              const key = (field.name || field.key) as string | undefined;
              if (key) {
                // Tentar obter o valor do config existente
                const existingConfig = node.config?.[key];
                acc[key] = existingConfig?.value ?? field.default ?? getDefaultValueForType(field.type as string);
              }
              return acc;
            }, {});

            // Definir ID canônico do node para uso consistente
            const canonicalNodeId = node.id;

            // Garantir que position existe e tem x,y válidos
            // Prioridade: 1) position do schema, 2) position atual salva, 3) grid padrão
            let position = node.position;
            
            if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') {
              // Tentar usar posição atual do node usando o ID canônico
              const currentPosition = currentPositions.get(canonicalNodeId);
              if (currentPosition) {
                position = currentPosition;
              } else {
                // Se não tem position nem no schema nem no estado atual, distribuir em grid 3 colunas
                const col = index % 3;
                const row = Math.floor(index / 3);
                position = {
                  x: 100 + (col * 400), // Espaçamento horizontal de 400px
                  y: 100 + (row * 200)  // Espaçamento vertical de 200px
                };
              }
            }

            return {
              id: node.id,
              type: 'customNode',
              position: position,
              data: {
                ...node.data,
                title: node.title || node.data?.title || nodeDefinition?.name || 'Unnamed Node',
                label: node.title || node.data?.label || nodeDefinition?.name || 'Unnamed Node',
                name: node.name || node.data?.name || nodeDefinition?.type || 'unknown',
                type: nodeDefinition?.type || node.data?.type || node.name || 'unknown',
                nodeId: resolvedNodeId ?? nodeDefinition?.id ?? rawNodeId ?? nodeType,
                workflowId,
                settings: nodeDefinition?.settings,
              },
              config: node.config || defaultValues
            };
          }));

          const updatedEdges = schemaEdges.map((edge: SchemaEdge) => ({
            ...edge,
            type: 'smoothEdge',
            data: {
              input: edge.input || edge.data?.input
            }
          }));

          setNodes(loadedNodes);
          setEdges(updatedEdges);
          const initialSchema: WorkflowSettings = {
            nodes: loadedNodes.map((node: Node) => formatNodeForSchema(node)),
            edges: updatedEdges.map((edge: Record<string, unknown>) => formatEdgeForSchema(edge)),
            ...(resolvedOrganizationId ? { organizationId: resolvedOrganizationId } : {}),
            ...(resolvedProductId ? { workspaceId: resolvedProductId } : {})
          };
          lastSavedSchemaRef.current = JSON.stringify(initialSchema);

          // Após carregar, marcar que não é mais o carregamento inicial
          setTimeout(() => {
            isInitialLoad.current = false;
          }, 500);
        }
      } catch (error) {
        console.error('Error loading workflow:', error);
        toast({
          title: t('common.error'),
          description: t('common.errorDescription'),
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadWorkflow();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, refreshKey]);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      const data = event.dataTransfer.getData('application/reactflow');

      if (!data || !reactFlowInstance || !reactFlowBounds) return;

      try {
        const nodeInfo = JSON.parse(data);
        const position = reactFlowInstance.project({
          x: event.clientX - reactFlowBounds.left,
          y: event.clientY - reactFlowBounds.top,
        });

        const newNode = createNewNode(nodeInfo, position, workflowId, getId);
        setNodes(nds => nds.concat(newNode));

      } catch (error) {
        console.error('Error adding node:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reactFlowInstance, nodes, edges, workflowId]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Função para validar conexões, permitindo múltiplas conexões dos mesmos handles
  const isValidConnection = useCallback((connection: Connection) => {
    // Sempre permitir conexões a partir de handles específicos (true, false, loop, exit)
    const specialHandles = ['true', 'false', 'loop', 'exit'];
    if (connection.sourceHandle && specialHandles.includes(connection.sourceHandle)) {
      return true;
    }
    
    // Para outros handles, verificar se já existe uma conexão
    const edges = reactFlowInstance?.getEdges() || [];
    return !edges.some(
      edge => 
        edge.source === connection.source && 
        edge.sourceHandle === connection.sourceHandle
    );
  }, [reactFlowInstance]);

  const onConnect = useCallback(
    async (params: Connection) => {
      if (!isValidConnection(params)) {
        return;
      }

      // Cria um novo edge com UUID gerado diretamente, sem o prefixo "reactflow_edge-"
      const newEdge = {
        id: getId(), // Usando a mesma função getId para gerar UUIDs
        source: params.source || '',
        target: params.target || '',
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        type: 'smoothEdge',
        data: {
          input: null, // Será configurado quando o usuário testar o workflow
          sourceHandle: params.sourceHandle, // Adicionando sourceHandle para garantir que seja preservado
          sourceNodeId: params.source, // Armazenar o ID do nó de origem para facilitar referência posterior
        }
      };

      // Verificar se é uma conexão de um nó condicional (handles true ou false)
      if (params.sourceHandle === 'true' || params.sourceHandle === 'false') {
        // Identificar o tipo de nó de origem para verificar se é um if_then
        const sourceNode = nodes.find(node => node.id === params.source);
        
        if (sourceNode && sourceNode.data?.type === 'if_then') {
          // Adicionar a condição nos metadados da aresta
          (newEdge.data as Record<string, unknown>).condition = params.sourceHandle; // 'true' ou 'false'
          
          // Adicionar também no formato metadata para compatibilidade com workflow-cleaner
          (newEdge as unknown as Record<string, unknown>).metadata = { condition: params.sourceHandle };
        }
      }

      // Adiciona a nova conexão aos edges existentes
      setEdges(eds => [...eds, newEdge as Edge]);

    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isValidConnection,
      setEdges
    ]
  );

  const saveWorkflow = useCallback(
    async (silent: boolean = false) => {
      try {
        const settings = buildWorkflowSettings();
        const serializedSchema = JSON.stringify(settings);

        if (serializedSchema === lastSavedSchemaRef.current) {
          return;
        }

        await WorkflowService.saveWorkflow(workflowId, settings);
        lastSavedSchemaRef.current = serializedSchema;

        if (!silent && onWorkflowChange) {
          onWorkflowChange();
        }

        if (!silent) {
          toast({
            title: t('common.success'),
            description: t('workflow.savedSuccessfully'),
          });
        }
      } catch (error) {
        if (!silent) {
          toast({
            title: t('common.error'),
            description: t('common.errorDescription'),
            variant: 'destructive',
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onWorkflowChange,
      reactFlowInstance,
      resolvedOrganizationId,
      resolvedProductId,
      t,
      toast,
      workflowId
    ]
  );

  useEffect(() => {
    if (isInitialLoad.current) {
      return;
    }

    const currentSettings = buildWorkflowSettings();
    const serializedSchema = JSON.stringify(currentSettings);

    if (serializedSchema === lastSavedSchemaRef.current) {
      return;
    }

    if (onUpdate) {
      onUpdate(true);
    }

    // NÃO salvar automaticamente - as alterações serão salvas apenas ao clicar em "Publicar"
    // O auto-save foi removido para seguir o fluxo de publicação manual
  }, [buildWorkflowSettings, onUpdate]);

  // Expor saveWorkflow via ref para chamada externa ao publicar
  useEffect(() => {
    if (saveRef) {
      saveRef.current = {
        saveWorkflow: async () => {
          await saveWorkflow(true);
        }
      };
    }
  }, [saveRef, saveWorkflow]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <PageLoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-[600px]">
      <div ref={reactFlowWrapper} className="flex-1 h-full">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          isValidConnection={isValidConnection}
          defaultEdgeOptions={{
            type: 'smoothEdge',
            animated: false
          }}
          connectionLineStyle={{ stroke: 'hsl(var(--muted-foreground) / 0.3)', strokeWidth: 2 }}
          connectionMode={ConnectionMode.Loose}
          onInit={setReactFlowInstance}
          onDrop={onDrop}
          onDragOver={onDragOver}
          defaultViewport={defaultViewport}
          fitView
          fitViewOptions={{
            padding: 0.5,
            maxZoom: 1,
            minZoom: 0.5,
          }}
          minZoom={0.5}
          maxZoom={1.5}
          proOptions={{ hideAttribution: true }}
          style={{ width: '100%', height: '100%' }}
        >
          <Controls className="bg-background border-border" />
          <Background className="bg-muted/5" gap={12} size={1} />
          <Panel position="top-right" className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => reactFlowInstance?.zoomIn()}
              className="h-8 w-8 bg-background"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => reactFlowInstance?.zoomOut()}
              className="h-8 w-8 bg-background"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
          </Panel>
          <Panel position="bottom-right" className="mr-4 mb-4">
            {currentWorkspace && (
              <WorkflowTestComponent
                organizationId={currentWorkspace.organization_id}
                workspaceId={currentWorkspace.id}
              />
            )}
          </Panel>
        </ReactFlow>
      </div>
      <Card className="w-80 h-full border-0 rounded-none border-l border-border overflow-hidden">
        <NodeList />
      </Card>
    </div>
  );
}
export function NewWorkflowEditor(props: WorkflowEditorProps) {
  return (
    <ReactFlowProvider>
      <WorkflowEditorContent {...props} />
    </ReactFlowProvider>
  );
}

export default NewWorkflowEditor;
